/** @jsxImportSource @emotion/react */
import React, { useEffect, useState, useRef, useMemo, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { DataType } from '@grapecity/wijmo';
import { SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import CustomGrid from 'components/grids/CustomGrid';
import { getMdIvestMdlTopSummaryData } from 'apis/md/MdInvestResult';
import { MdIvestMdlTopSummary } from 'models/md/MdInvestResult';

/**
 * @desc 투자비 산출 등록/조회 > 투자비 산출 등록 > 투자비 산출 결과 분석 탭 > 투자항목별 Top 10
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.05.14   jdseong       신규 생성
 */
interface Props {
  elmPrjId?: string;
  ivesMdlId?: string;
}

const InvestmentItemsRanking = ({ elmPrjId, ivesMdlId, ...props }: Props, ref) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const [rowData, setRowData] = useState<MdIvestMdlTopSummary[]>([]);

  useImperativeHandle(ref, () => ({
    search: (elmPrjId, ivesMdlId) => {
      handleSearch(elmPrjId, ivesMdlId);
    },
  }));

  useEffect(() => {
    handleSearch(elmPrjId, ivesMdlId);
  }, [elmPrjId, ivesMdlId]);

  // useEffect(() => {
  //   changeGridHeight((rowData || []).length);
  // }, [rowData]);

  const handleSearch = (elmPrjId, ivesMdlId) => {
    getMdIvestMdlTopSummaryData(elmPrjId, ivesMdlId).then((result) => {
      setRowData(result);
    });
  };

  // const changeGridHeight = useEvent((rowCount = 0) => {
  //   const rowHeight = 37;
  //   const defHeight = 376;
  //   if (rowCount < 1) {
  //     setGridHeight(defHeight);
  //   } else {
  //     setGridHeight(Math.min(rowHeight * rowCount + 12, defHeight));
  //   }
  // });

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        width: 40,
        align: 'center',
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'prdnProcCdNm',
        header: String(t('com.label.공정', '공정')),
        width: 90,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'eqclNm',
        header: String(t('md.label.설비군', '설비군')),
        width: 120,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'ivesAtcNm',
        header: String(t('md.label.투자항목', '투자항목')),
        width: 130,
        align: 'left',
        isReadOnly: true,
      },
      {
        binding: 'ivesDtlDesc',
        header: String(t('md.label.투자상세', '투자상세')),
        width: '*',
        minWidth: 140,
        align: 'left',
        isReadOnly: true,
      },
      {
        binding: 'eqpCnt',
        header: String(t('md.label.설비대수', '설비대수')),
        width: 60,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'quotUprc',
        header: String(t('md.label.단가\n(억원)', '단가\n(억원)')),
        width: 80,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'totalInvstAmt',
        header: String(t('md.label.투자비\n(억원)', '투자비\n(억원)')),
        width: 80,
        align: 'center',
      },
    ];
  }, []);

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.autoRowHeights = true;

    grid.formatItem.addHandler((s, e) => {
      if (e.panel === s.columnHeaders) {
        e.cell.style.fontWeight = '700';
        e.cell.style.display = 'flex';
        e.cell.style.alignItems = 'center';
        e.cell.style.justifyContent = 'center';
        const text = e.cell.textContent ?? '';
        if (text.includes('\n')) {
          e.cell.innerHTML = text.replace(/\n/g, '<br/>');
        }
      }
    });
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.투자항목별 Top 10', '투자항목별 Top 10')}</h3>
        </SubTitleGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={376}
        isSelector={false}
        isFilter={false}
        isReadOnly={true}
        allowPinning={false}
        initialized={onInitialized}
      />
    </>
  );
};

export default React.forwardRef(InvestmentItemsRanking);
